import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { setFileExclusion } from '../api/data-connectors/file-exclusion'
import type { FileInfoType } from '../types'
import { useNotification } from './useNotification'

export function useSetFileExclusion() {
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const { t } = useTranslation()

  // "Via the UI" optimistic pattern:
  // isPending + variables in FileCard flip the icon instantly during the request.
  // onSuccess writes the confirmed server value straight into the cache (no refetch).
  // invalidateQueries would also work in principle, but the GET /filepool response
  // and the isPending→false state change land in different React render batches,
  // causing the icon to briefly revert to the old value before the refetch resolves.
  return useMutation({
    mutationFn: ({ fileId, isExcluded }: { fileId: string; isExcluded: boolean }) =>
      setFileExclusion(fileId, isExcluded),
    onSuccess: (data) => {
      queryClient.setQueryData<FileInfoType[]>(['filepool'], (old) => {
        if (!old) return old
        return old.map((file) =>
          file.id === data.fileId ? { ...file, isExcluded: data.isExcluded } : file
        )
      })
    },
    onError: () => {
      // isPending going false automatically reverts displayedIsExcluded back to the prop value —
      // no cache restore needed; just notify the user
      notify({ isSuccess: false, text: t('files.excludeFailureToast'), position: 'bottom' })
    },
  })
}
